import React, { useEffect, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import { useTrust, TrustNote, scoreLabelText } from './Trust';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://larosa-negra.com').replace(/\/$/, '');

// Hotel + AggregateRating + Review[] — the same graph the build-time prerender emits, so JS and non-JS crawlers agree.
export const buildReviewSchema = (testimonials, hotelInfo) => ({
  '@context': 'https://schema.org',
  '@type': 'Hotel',
  '@id': `${SITE_URL}/#hotel`,
  name: 'Hotel La Rosa Negra',
  url: `${SITE_URL}/`,
  image: `${SITE_URL}/og-image.jpg`,
  address: { '@type': 'PostalAddress', streetAddress: 'Kepi i Gjeranave, Long Beach (Velika Plaža)', addressLocality: 'Ulcinj', postalCode: '85360', addressCountry: 'ME' },
  aggregateRating: { '@type': 'AggregateRating', ratingValue: String(hotelInfo?.rating ?? 8.0), reviewCount: String(hotelInfo?.reviewCount ?? testimonials.length), bestRating: '10', worstRating: '1' },
  review: testimonials.filter((r) => r.text && r.name).map((r) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: r.name },
    datePublished: r.date,
    reviewBody: r.text,
    inLanguage: r.lang || 'en',
    reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '10', worstRating: '1' },
    ...(r.source ? { publisher: { '@type': 'Organization', name: r.source } } : {}),
  })),
});

const Reviews = ({ withSchema = false }) => {
  const { testimonials, hotelInfo } = useHotel();
  const { t, lang } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [google, setGoogle] = useState(null);
  useEffect(() => { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/google-rating`).then((r) => r.json()).then(setGoogle).catch(() => {}); }, []);
  const trust = useTrust();
  const liveScore = Number(trust?.booking?.score ?? hotelInfo.rating ?? 0);
  const liveCount = trust?.booking?.count ?? hotelInfo.reviewCount;

  useEffect(() => {
    const id = 'reviews-jsonld';
    document.getElementById(id)?.remove();
    if (!withSchema || !testimonials?.length) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json'; el.id = id;
    el.textContent = JSON.stringify(buildReviewSchema(testimonials, hotelInfo));
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, [testimonials, hotelInfo, withSchema]);

  // Map language code to JS locale for date formatting
  const dateLocale = { en: 'en-US', me: 'sr-Latn-ME', sq: 'sq-AL', de: 'de-DE', ru: 'ru-RU', pl: 'pl-PL', hu: 'hu-HU' }[lang] || 'en-US';
  const [showOriginal, setShowOriginal] = useState({});
  const langName = (code) => { try { return new Intl.DisplayNames([dateLocale], { type: 'language' }).of(code === 'me' ? 'sr-Latn' : code) || code; } catch { return code; } };
  const reviewText = (review, index) => {
    const tr = review.translations?.[lang]?.text;
    if (!tr || tr === review.text || review.lang === lang) return { text: review.text, translated: false };
    const original = !!showOriginal[index];
    return { text: original ? review.text : tr, translated: true, original };
  };

  const nextReview = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevReview = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-rotate every 6 s; paused while hovered / touched / focused, when the tab is hidden, or if the visitor prefers reduced motion.
  const [paused, setPaused] = useState(false);
  const [touchedAt, setTouchedAt] = useState(0);
  useEffect(() => {
    if (paused || testimonials.length < 2) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const tick = () => { if (!document.hidden && Date.now() - touchedAt > 8000) setCurrentIndex((prev) => (prev + 1) % testimonials.length); };
    const id = setInterval(tick, 6000);
    return () => clearInterval(id);
  }, [paused, touchedAt, testimonials.length]);
  const pauseProps = {
    onMouseEnter: () => setPaused(true), onMouseLeave: () => setPaused(false),
    onFocus: () => setPaused(true), onBlur: () => setPaused(false),
    onTouchStart: () => setTouchedAt(Date.now()), onPointerDown: () => setTouchedAt(Date.now()),
  };

  const active = testimonials[currentIndex];
  const activeKey = active ? (active.id || `${active.name}-${active.date}-${currentIndex}`) : 'none';
  const activeText = active ? reviewText(active, currentIndex) : { text: '' };
  const activeParts = activeText.text.split(' − ').map((part, pi) => (
    <span key={pi} className={pi > 0 ? 'block mt-2 text-base text-gray-500' : 'italic'}>{pi > 0 ? '− ' : '"'}{part}{pi === 0 && !activeText.text.includes(' − ') ? '"' : ''}</span>
  ));
  const dots = testimonials.map((review, index) => (
    <button key={review.id || `dot-${review.name}-${index}`} type="button" aria-label={`Review ${index + 1}`} onClick={() => setCurrentIndex(index)} data-testid={`review-dot-${index}`}
      className={`h-2 rounded-full transition-all ${index === currentIndex ? 'bg-orange-500 w-8' : 'bg-gray-300 w-2'}`} />
  ));
  return (
    <section id="reviews" className="py-16 bg-white" data-testid="reviews-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-orange-500 font-semibold mb-2 text-sm uppercase tracking-wider">{t('reviews.eyebrow')}</p>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900">{t('reviews.heading')}</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('reviews.lead')}
          </p>
        </div>

        {/* Overall Rating — live Booking.com score, unedited */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 lg:p-8 text-center border-2 border-blue-200" data-testid="reviews-score-block">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="text-6xl font-bold text-blue-600" data-testid="reviews-score">{liveScore.toFixed(1)}</div>
              <div className="text-left">
                <p className="text-gray-700 font-semibold" data-testid="reviews-score-label">{scoreLabelText(lang, liveScore)}</p>
                <p className="text-gray-600 text-sm" data-testid="reviews-count">{liveCount} {t('reviews.reviewsCount')} · Booking.com</p>
              </div>
            </div>
            {trust?.booking?.subscores && (
              <div className="flex flex-wrap justify-center gap-2 mb-4" data-testid="reviews-subscores">
                {Object.entries(trust.booking.subscores).map(([k, v]) => (
                  <span key={k} className={`text-xs px-2.5 py-1 rounded-full border ${v >= 8 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-white border-blue-200 text-gray-700'}`}>{k} <b>{v.toFixed(1)}</b></span>
                ))}
              </div>
            )}
            {google?.configured && google.rating && (
              <a href={google.maps_url || 'https://maps.app.goo.gl/LEUHhov5cBzN9jvz6'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white border rounded-full px-4 py-2 mb-4 text-sm font-semibold text-gray-800 hover:shadow" data-testid="google-rating-badge">
                <span className="font-bold text-blue-600">G</span> {google.rating} <span className="text-orange-400">★</span> <span className="text-gray-500 font-normal">· {google.count} Google reviews</span>
              </a>
            )}
            <div className="flex items-center justify-center gap-2 text-orange-500">
              {[...Array(hotelInfo.stars)].map((_, i) => (
                <span key={i} className="text-2xl">★</span>
              ))}
              <span className="text-gray-700 ml-2 font-semibold">{t('reviews.starHotel')}</span>
            </div>
            <TrustNote className="justify-center text-gray-500 mt-4" />
          </div>
        </div>

        {/* Testimonials Carousel */}
        <div className="max-w-5xl mx-auto">
          <div className="relative" {...pauseProps} data-testid="reviews-carousel" data-paused={paused ? 'true' : 'false'}>
            {/* Navigation buttons */}
            <button 
              onClick={prevReview}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 lg:-translate-x-12 bg-white rounded-full p-3 shadow-lg hover:bg-gray-50 transition-colors z-10"
            >
              <ChevronLeft size={24} className="text-gray-700" />
            </button>
            <button 
              onClick={nextReview}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 lg:translate-x-12 bg-white rounded-full p-3 shadow-lg hover:bg-gray-50 transition-colors z-10"
            >
              <ChevronRight size={24} className="text-gray-700" />
            </button>

            {/* Review card — only the active one is rendered so the block is exactly as tall as its text */}
            {active && (
              <div key={activeKey} className="px-4 animate-in fade-in slide-in-from-right-4 duration-300" data-testid="review-card">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 lg:p-8 shadow-xl border border-gray-100">
                  <Quote size={32} className="text-orange-500 mb-3 opacity-50" />
                  <p className="text-gray-700 text-lg lg:text-xl leading-relaxed mb-2" lang={activeText.translated && !activeText.original ? lang : (active.lang || undefined)} data-testid={`review-text-${currentIndex}`}>{activeParts}</p>
                  {activeText.translated && (
                    <p className="text-xs text-gray-400 mb-3" data-testid={`review-translated-${currentIndex}`}>
                      {t('reviews.translatedFrom').replace('{lang}', langName(active.lang || 'en'))} ·{' '}
                      <button type="button" onClick={() => setShowOriginal((s) => ({ ...s, [currentIndex]: !s[currentIndex] }))} className="underline hover:text-orange-600" data-testid={`review-toggle-original-${currentIndex}`}>
                        {activeText.original ? t('reviews.showTranslation') : t('reviews.showOriginal')}
                      </button>
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-4 mt-3">
                    <div>
                      <p className="font-bold text-lg text-gray-900" data-testid={`review-author-${currentIndex}`}>{active.name}{active.country ? <span className="text-gray-500 font-normal text-base"> · {active.country}</span> : null}</p>
                      <p className="text-gray-500 text-sm">{new Date(active.date).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long', day: 'numeric' })}{active.source ? <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold" data-testid={`review-source-${currentIndex}`}>{active.source}</span> : null}</p>
                    </div>
                    <div className="flex items-center gap-1 bg-orange-100 px-4 py-2 rounded-full shrink-0">
                      <Star size={18} className="text-orange-500 fill-current" />
                      <span className="font-bold text-orange-600">{active.rating}/10</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-5">{dots}</div>
          </div>
        </div>

        {/* Booking.com link */}
        <div className="text-center mt-8">
          <p className="text-gray-600 mb-4">{t('reviews.readMoreOn')}</p>
          <a
            href={hotelInfo.bookingComUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
            data-testid="reviews-booking-com-link"
          >
            Booking.com
          </a>
        </div>
      </div>
    </section>
  );
};

export default Reviews;